import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import DOMPurify from 'dompurify';
import {
  makeStyles, mergeClasses, tokens,
  Input, Button, Accordion, AccordionItem, AccordionHeader, AccordionPanel,
} from '@fluentui/react-components';
import { Heading, Container, Card, EmptyState } from '../theme/primitives';
import { stonePalette } from '../theme/brand';
import { iconMap } from '../theme/icons';
import { CONTACT_PHONE, CONTACT_PHONE_HREF, CONTACT_EMAIL, CONTACT_EMAIL_HREF } from '../config.js';
import { API_BASE } from '../config.js';
import dukHeroImg from '../assets/img/marketing/duk-faq.jpg';

const SearchIcon = iconMap.search;
const SearchOffIcon = iconMap.search_off;
const CloseIcon = iconMap.close;
const HelpIcon = iconMap.help_outline;
const SupportIcon = iconMap.support_agent;
const PhoneIcon = iconMap.phone;
const MailIcon = iconMap.mail;
const CheckIcon = iconMap.check;

// Fiksuota rikiavimo tvarka — nauji, nekategorizuoti DUK įrašai (be `category`
// lauko) patenka į "Kita" pagal backend schemos numatytąją reikšmę. `name`
// TURI likti lietuviškas — tai realaus DB `category` lauko reikšmė, pagal
// kurią grupuojama (žr. groups useMemo žemiau); `labelKey` naudojamas TIK
// rodomam antraštės tekstui.
const DUK_CATEGORIES = [
  { name: 'Užsakymas', labelKey: 'duk.cat_order', icon: iconMap.shopping_cart },
  { name: 'Gamyba ir medžiagos', labelKey: 'duk.cat_production', icon: iconMap.category },
  { name: 'Pristatymas ir surinkimas', labelKey: 'duk.cat_delivery', icon: iconMap.local_shipping },
  { name: 'Mokėjimas', labelKey: 'duk.cat_payment', icon: iconMap.credit_card },
  { name: 'Garantija ir sauga', labelKey: 'duk.cat_warranty', icon: iconMap.shield },
  { name: 'Kita', labelKey: 'duk.cat_other', icon: iconMap.help_outline },
];

const stripHtml = s => s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

// Senesni įrašai turinį saugojo kaip draft-js "raw" JSON — čia jį konvertuojame
// į HTML, kad senos DUK atsakymai nerodytų neapdorotos JSON teksto vartotojui.
function faqContentHtml(raw) {
  if (!raw) return '';
  let html = raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.blocks)) {
      html = stateToHTML(convertFromRaw(parsed));
    }
  } catch {
    // jau HTML — naudojame kaip yra
  }
  return DOMPurify.sanitize(html);
}

const useStyles = makeStyles({
  // Standartinis 2 stulpelių hero — tas pats paternas, kaip /meistro-uzsakymas
  // (Carpenter.jsx) ir kt. (žr. 20 punktų sąrašo #6/#8): balta sekcija, kairėje
  // eyebrow+title+tekstas+punktai, dešinėje nuotrauka.
  hero: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Paddingas ant heroInner (ne hero) — kai maxWidth-centravimas IR paddingas
  // yra ant SKIRTINGŲ elementų, susidaro dvigubas centravimas, kuris duoda
  // kitokį kairįjį atitraukimą nei header'io. Sudėjus abu ant to paties
  // elemento, formulė identiška TopBar.jsx/MainMenu.jsx `container` (žr.
  // /admin/bugs "Header logotipas ir language icon turi lygiuoti su
  // puslapio turiniu").
  heroInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, alignItems: 'center',
    '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  heroEyebrow: { marginBottom: tokens.spacingVerticalS },
  title: { marginBottom: tokens.spacingVerticalM },
  heroText: { fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground2, lineHeight: '1.6', marginBottom: tokens.spacingVerticalL, maxWidth: '32rem' },
  heroPoints: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroPoint: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground1 },
  heroPointIcon: { color: tokens.colorBrandForeground2, fontSize: '18px', flexShrink: 0 },
  heroImageWrap: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    aspectRatio: '4 / 3',
  },
  heroImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  faqSection: {
    backgroundColor: stonePalette[50],
    paddingTop: '5rem', paddingBottom: '5rem',
  },
  // Paddingas ant paties Container'io (ne faqSection) — Container turi savo
  // maxWidth+auto-margin centravimą, ir jei paddingas būtų ant IŠORINĖS
  // sekcijos, susidarytų dvigubas centravimas (skirtingas kairysis atitraukimas
  // nei hero/header'io, žr. heroInner komentarą aukščiau ir /admin/bugs
  // "turinys ir sekcijos turi būti tokio pačio pločio").
  faqContainer: {
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  // Paieška ir "Vis dar turite klausimų?" kortelė lieka siauro, skaitomo pločio
  // ir centruotos — TIK pačios accordion kategorijos naudoja pilną Container'io
  // plotį (žr. #6), padalintos į 2 stulpelius nuo 900px.
  narrowWrap: { maxWidth: '42rem', marginLeft: 'auto', marginRight: 'auto' },
  searchWrap: { marginBottom: tokens.spacingVerticalXXL },
  searchInput: { width: '100%' },
  categoryGroup: { marginBottom: tokens.spacingVerticalXXL, ':last-child': { marginBottom: 0 } },
  categoryHeader: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  categoryIconChip: {
    width: '32px', height: '32px', borderRadius: tokens.borderRadiusMedium, flexShrink: 0,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  categoryTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, margin: 0 },
  list: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingVerticalS, alignItems: 'start',
    '@media (min-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)', columnGap: tokens.spacingHorizontalL },
  },
  item: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationNormal,
  },
  itemOpen: { boxShadow: tokens.shadow8 },
  header: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  panelDivider: { height: '1px', backgroundColor: tokens.colorNeutralStroke2, marginBottom: tokens.spacingVerticalL },
  content: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.6',
    '& p': { marginBottom: tokens.spacingVerticalS },
    '& p:last-child': { marginBottom: 0 },
    '& ul': { listStyleType: 'disc', marginLeft: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalS },
    '& ol': { listStyleType: 'decimal', marginLeft: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalS },
    '& li': { marginBottom: tokens.spacingVerticalXXS },
    '& strong': { fontWeight: tokens.fontWeightSemibold },
    '& a': { color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline' },
  },
  ctaCard: { marginTop: '3.5rem', padding: tokens.spacingHorizontalXXL, textAlign: 'center', maxWidth: '42rem', marginLeft: 'auto', marginRight: 'auto' },
  ctaIconChip: {
    width: '48px', height: '48px', borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalM,
  },
  ctaTitle: { marginBottom: tokens.spacingVerticalXS },
  ctaText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalL, marginTop: 0 },
  ctaButtons: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalS, justifyContent: 'center',
    '@media (min-width: 640px)': { flexDirection: 'row' },
  },
});

function Duk() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const s = useStyles();

  useEffect(() => { document.title = `${t('duk.title')} | Fitit`; }, [t]);

  useEffect(() => {
    fetch(`${API_BASE}/duk`)
      .then(r => r.json())
      .then(d => setFaqs(Array.isArray(d) ? d.filter(f => f.published !== false) : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return faqs;
    const q = search.toLowerCase();
    return faqs.filter(f =>
      f.title?.toLowerCase().includes(q) || stripHtml(faqContentHtml(f.content)).toLowerCase().includes(q)
    );
  }, [faqs, search]);

  const groups = useMemo(() => {
    return DUK_CATEGORIES
      .map(cat => ({ ...cat, items: filtered.filter(f => (f.category || 'Kita') === cat.name) }))
      .filter(g => g.items.length > 0);
  }, [filtered]);

  return (
    <div>

      {/* Hero */}
      <div className={s.hero}>
        <div className={s.heroInner}>
          <div>
            <Heading as="h1" variant="h1" className={s.title}>{t('duk.title')}</Heading>
            <p className={s.heroText}>
              {t('duk.hero_text')}
            </p>
            <div className={s.heroPoints}>
              <div className={s.heroPoint}><CheckIcon className={s.heroPointIcon} />{t('duk.hero_point_0')}</div>
              <div className={s.heroPoint}><CheckIcon className={s.heroPointIcon} />{t('duk.hero_point_1')}</div>
              <div className={s.heroPoint}><CheckIcon className={s.heroPointIcon} />{t('duk.hero_point_2')}</div>
            </div>
          </div>
          <div className={s.heroImageWrap}>
            <img src={dukHeroImg} alt={t('duk.img_alt')} className={s.heroImage} />
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className={s.faqSection}>
        <Container size="xl" className={s.faqContainer}>

          {/* Search */}
          <div className={mergeClasses(s.searchWrap, s.narrowWrap)}>
            <Input
              className={s.searchInput}
              contentBefore={<SearchIcon fontSize={20} />}
              contentAfter={search ? (
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<CloseIcon fontSize={18} />}
                  onClick={() => setSearch('')}
                  aria-label={t('duk.clear_search')}
                />
              ) : undefined}
              placeholder={t('duk.search_ph')}
              value={search}
              onChange={e => { setSearch(e.target.value); setExpanded(null); }}
            />
          </div>

          {loading ? (
            <EmptyState icon={<HelpIcon fontSize={26} />} title={t('duk.loading')} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<SearchOffIcon fontSize={26} />}
              title={search ? t('duk.no_results_for', { search }) : t('duk.no_results')}
            />
          ) : (
            groups.map(group => {
              const GroupIcon = group.icon;
              return (
                <div key={group.name} className={s.categoryGroup}>
                  <div className={s.categoryHeader}>
                    <div className={s.categoryIconChip}>{GroupIcon && <GroupIcon fontSize={16} />}</div>
                    <h2 className={s.categoryTitle}>{t(group.labelKey)}</h2>
                  </div>
                  <Accordion
                    collapsible
                    openItems={expanded === null ? [] : [expanded]}
                    onToggle={(_, data) => setExpanded(data.openItems.length ? data.openItems[0] : null)}
                    className={s.list}
                  >
                    {group.items.map(faq => {
                      const isOpen = expanded === faq._id;
                      return (
                        <AccordionItem key={faq._id} value={faq._id} className={mergeClasses(s.item, isOpen && s.itemOpen)}>
                          <AccordionHeader className={s.header} expandIconPosition="end">{faq.title}</AccordionHeader>
                          <AccordionPanel>
                            <div className={s.panelDivider} />
                            <div className={s.content} dangerouslySetInnerHTML={{ __html: faqContentHtml(faq.content) }} />
                          </AccordionPanel>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </div>
              );
            })
          )}

          {/* Still have questions */}
          <Card className={s.ctaCard}>
            <div className={s.ctaIconChip}>
              <SupportIcon fontSize={24} />
            </div>
            <Heading as="h3" variant="h3" className={s.ctaTitle}>{t('duk.cta_title')}</Heading>
            <p className={s.ctaText}>
              {t('duk.cta_text')}
            </p>
            <div className={s.ctaButtons}>
              <Button as="a" href={CONTACT_PHONE_HREF} appearance="primary" icon={<PhoneIcon fontSize={17} />}>
                {CONTACT_PHONE}
              </Button>
              <Button as="a" href={CONTACT_EMAIL_HREF} appearance="outline" icon={<MailIcon fontSize={17} />}>
                {CONTACT_EMAIL}
              </Button>
            </div>
          </Card>

        </Container>
      </section>

    </div>
  );
}

export default Duk;
